import PreviewCardHeader from 'Components/Common/PreviewCardHeader'
import UserFormFields from 'Components/user/UserFormFields';
import UserPrivileges from 'Components/user/UserPrivileges';
import { PRIVILEGE } from 'constants/privilege';
import { useFormik } from 'formik';
import withAccessControl from 'hoc/withAccessControl';
import useDocumentTitle from 'hooks/useDocumentTitle';
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Container, Row, Col, Card, CardBody, CardFooter, Spinner, Form } from 'reactstrap'
import { createNewUser } from 'services/api/users';
import { fetchRoles } from 'slices/users/roles/thunk';
import { fetchUserList } from 'slices/users/thunk';
import { AppDispatch, RootState } from 'store';
import { NewUser, UserRole } from 'types/users';
import { getErrorMessage } from 'utils/helpers';
import * as Yup from "yup";

export interface UserFormValues {
    name: string,
    email: string,
    role: UserRole | null,
}

const CreateUser = () => {

    useDocumentTitle("Create User");

    const dispatch: AppDispatch = useDispatch();
    const selectedHub = useSelector((state: RootState) => state.auth.selectedHub);

    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const defaultFormValues: UserFormValues = {
        name: '',
        email: '',
        role: null,
    };

    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: defaultFormValues,
        validationSchema: Yup.object({
            name: Yup.string().required("User name field can't be empty"),
            email: Yup.string().required("User email field can't be empty").email("Please enter a valid email address"),
        }),
        onSubmit: async (values) => {
            try {
                const newUser: NewUser = {
                    ...values,
                    role: values.role?.slug ?? ''
                }
                setLoading(true);
                await createNewUser(selectedHub?.uuid!, newUser);
                dispatch(fetchUserList({ page: 1, force: true }))
                toast.success("User created successfully!");
                navigate('/user');
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to create new user'));
            } finally {
                setLoading(false)
            }
        }
    });

    useEffect(() => {
        dispatch(fetchRoles(false));
    }, [dispatch])

    return (
        <div className="page-content">
            <Container fluid>
                <Form className='needs-validation' onSubmit={(e) => {
                    e.preventDefault();
                    formFields.handleSubmit();
                    return false;
                }}>
                    <Row>
                        <Col xl={6}>

                            <Card>
                                <PreviewCardHeader title="Create User" />
                                <CardBody>
                                    <UserFormFields formFields={formFields} />
                                </CardBody>
                                <CardFooter>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <button type='button' onClick={() => navigate('/user')} className="btn btn-outline-primary d-flex align-items-center">
                                            <i className='bx bx-left-arrow-alt fs-4'></i> Back
                                        </button>
                                        <button type='submit' disabled={loading} className="btn d-flex align-items-center btn-primary">
                                            {
                                                loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <React.Fragment>Save <i className='bx fs-4 bx-right-arrow-alt' ></i></React.Fragment>
                                            }
                                        </button>
                                    </div>
                                </CardFooter>
                            </Card>
                        </Col>
                        {
                            formFields.values.role &&
                            <Col xl={6}>
                                <Card>
                                    <PreviewCardHeader title="Privileges" />
                                    <CardBody>
                                        <UserPrivileges selectedRole={formFields.values.role} />
                                    </CardBody>
                                </Card>
                            </Col>
                        }
                    </Row>
                </Form>
            </Container>
        </div>
    )
}

export default withAccessControl(CreateUser, PRIVILEGE.HUB.USER.CREATE)